/**
 * Shared decision-read math + copy for the A/B experiment views.
 *
 * The staff detail page and the client running/completed views render the same
 * three statistical reads on the pooled conversion difference — an always-valid
 * confidence sequence (the live stop-decision read), a Bayesian posterior ("how
 * likely / how big"), and the derived significance flag — but through different
 * layouts. Per the sibling `experimentMetricDescriptors` philosophy they must
 * NOT share a layout, but they MUST share the *math and the wording*, or the two
 * views silently drift on how a lift is judged significant or explained. These
 * pure helpers (and the tooltip copy) are that single source of truth; both
 * views import them and wrap them in their own presentation.
 */
import {
    type AlwaysValidRateDiffResult,
    alwaysValidRateDiffCi,
    type BayesianRateDiffResult,
    bayesianRateDiffPosterior,
} from './alwaysValidCi';
import type { ExperimentPooledRow } from './experimentMetricDescriptors';

/** The pooled row for one arm, or null when it hasn't been observed yet. */
export function findPooledVariant(
    rows: ExperimentPooledRow[],
    variant: string
): ExperimentPooledRow | null {
    return rows.find((r) => r.variant === variant) ?? null;
}

/**
 * Always-valid (confidence-sequence) CI on the pooled conversion-rate
 * difference, from each arm's observed rate and visitor count. Unlike the
 * fixed-sample Wilson interval, its coverage holds at every look at once, so
 * stopping the moment it excludes zero is a valid decision, not a peeking
 * artifact. This is the live read both views monitor.
 */
export function conversionAlwaysValidCi(
    pooled: ExperimentPooledRow[]
): AlwaysValidRateDiffResult | null {
    const treatment = findPooledVariant(pooled, 'treatment');
    const control = findPooledVariant(pooled, 'control');
    return alwaysValidRateDiffCi({
        pTreatment: treatment?.conversion_rate,
        nTreatment: treatment?.visitors,
        pControl: control?.conversion_rate,
        nControl: control?.visitors,
    });
}

/** Bayesian posterior on the pooled conversion-rate difference. */
export function conversionBayesianPosterior(
    pooled: ExperimentPooledRow[]
): BayesianRateDiffResult | null {
    const treatment = findPooledVariant(pooled, 'treatment');
    const control = findPooledVariant(pooled, 'control');
    return bayesianRateDiffPosterior({
        cTreatment: treatment?.conversions,
        nTreatment: treatment?.visitors,
        cControl: control?.conversions,
        nControl: control?.visitors,
    });
}

/**
 * Significance of the pooled conversion-lift under continuous monitoring: the
 * always-valid CI on the difference excludes zero. 'positive' when the lower
 * bound is above zero, 'negative' when the upper bound is below zero, otherwise
 * null (not significant yet).
 */
export function pooledSignificance(pooled: ExperimentPooledRow[]): 'positive' | 'negative' | null {
    const avci = conversionAlwaysValidCi(pooled);
    if (avci == null) return null;
    if (avci.low > 0) return 'positive';
    if (avci.high < 0) return 'negative';
    return null;
}

/** Tooltip explaining the always-valid (confidence-sequence) read shown live. */
export const ALWAYS_VALID_TOOLTIP =
    'Always-valid interval (confidence sequence). Safe to check every day: only a 5% chance it ever misses the true lift, across all days at once — so stopping the moment it excludes zero is legitimate. It is wider than a one-look CI; that extra width is the price of being able to peek without inflating false positives.';

/** Tooltip explaining the gated fixed-sample final read. */
export const FIXED_SAMPLE_TOOLTIP =
    'Fixed-sample 95% interval (Wilson). Valid for one look, at the pre-planned sample size — which is why it unlocks only once enough visitors have arrived. Peeking at it repeatedly or stopping as soon as it turns green breaks this guarantee and inflates false positives. Use it for the final read / external quote.';

/** Tooltip explaining the Bayesian posterior read (chance of winning). */
export const BAYESIAN_TOOLTIP =
    "Bayesian posterior probability that Rose's true conversion lift is positive, from a Beta-Binomial model (Jeffreys prior) on each arm — accurate even at low conversion rates and with few conversions. The best-estimate lift is regularised toward zero, correcting the winner's-curse bias where an early, lucky-looking lift overstates the real one. The bracket is the 95% credible interval — a Bayesian belief about the true lift, NOT the always-valid stopping bar.";
